import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Mail, ArrowRight, User, KeyRound, Smartphone, MailCheck } from 'lucide-react'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { passwordFailureKey } from '@/lib/passwordRule'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import { apiErrorText, apiFieldErrors } from '@/lib/sdk-official/resources/member-import'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { MemberFieldsForm } from '@/components/features/members/MemberFieldsForm'
import { apiValues, missingRequired, type FieldWithValue } from '@/components/features/members/memberFields'
import { TermsNotice } from './TermsNotice'
import { AgeConfirmBox } from './AgeConfirmBox'
import { minimumAgeOf } from '@/lib/ageGate'
import { authFailureText } from '@/lib/authFailure'
import { PhoneInput, type PhoneValue } from '@/components/ui/phone-input'
import { countryFromLocale } from '@/lib/phone'
import { PhoneVerification } from '@/components/features/sms/PhoneVerification'
import { loginConsentVersion, markLoginConsentSeen, signupConsentOf, useConsentTexts } from '@/components/features/sms/smsShared'

interface SignupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const SignupDialog = ({ open, onOpenChange }: SignupDialogProps) => {
  const { t, i18n } = useTranslation(['auth', 'members', 'sms'])
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [passwordError, setPasswordError] = React.useState('')
  /* F-85: the community's own signup fields (required ones plus those the
     team marked "ask on signup"), in the team's order. */
  const [signupFields, setSignupFields] = React.useState<FieldWithValue[]>([])
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({})
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({})
  /* F-142: optional phone, one unchecked box for notices and news (SMS and WhatsApp), and the code step after the account exists. */
  const [phone, setPhone] = React.useState<PhoneValue>({ phone: '', phoneCountry: '' })
  const [phoneConsent, setPhoneConsent] = React.useState(false)
  const [verifyPhone, setVerifyPhone] = React.useState<PhoneValue | null>(null)
  /* F-150: in a community that approves signups the form asks to join (no password) and ends on "request sent". */
  const [requestSent, setRequestSent] = React.useState(false)
  /* F-204: "I am N or older", unchecked; the form does not go on without it. */
  const [ageConfirmed, setAgeConfirmed] = React.useState(false)
  const [ageError, setAgeError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setPhone({ phone: '', phoneCountry: '' })
      setPhoneConsent(false)
      setVerifyPhone(null)
      setRequestSent(false)
      setAgeConfirmed(false)
      setAgeError(null)
      setName('')
      setEmail('')
      setPassword('')
      setPasswordError('')
      setError('')
      setFieldValues({})
      setFieldErrors({})
      return
    }
    let active = true
    sdk.memberFields.signup()
      .then((list) => {
        if (!active) return
        setSignupFields([...list].sort((a, b) => a.order - b.order).map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type,
          options: f.options ?? null,
          required: !!f.required,
          minSelections: f.minSelections ?? null,
          maxSelections: f.maxSelections ?? null,
          patternMessage: f.patternMessage ?? null,
          value: ''
        })))
      })
      /* Without the list the form still submits; a required field the API
         misses comes back as a 422 shown below. */
      .catch(() => { if (active) setSignupFields([]) })
    return () => { active = false }
  }, [open])

  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const { signUp } = useAuth()
  const { community } = useCommunity()
  const consentTexts = useConsentTexts(community?.id, i18n.language)
  const asksToJoin = Boolean(community?.signupEnabled && community?.signupRequiresApproval)
  const minimumAge = minimumAgeOf(community)

  const validatePassword = (pass: string) => {
    const key = passwordFailureKey(pass)
    return key ? t(key) : ''
  }


  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value
    setPassword(newPassword)
    setPasswordError(validatePassword(newPassword))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const passwordValidation = asksToJoin ? '' : validatePassword(password)
    if (passwordValidation) {
      setError(passwordValidation)
      return
    }
    const missing = missingRequired(signupFields, fieldValues, (label) => t('members:fieldsSection.requiredError', { label }))
    setFieldErrors(missing)
    const ageMissing = ageConfirmed ? null : t('ageGate.required', { age: minimumAge })
    setAgeError(ageMissing)
    if (Object.keys(missing).length || ageMissing) return

    setIsLoading(true)
    setError('')

    const fields = apiValues(signupFields, Object.fromEntries(Object.entries(fieldValues).filter(([, value]) => value.trim()))) as Record<string, string | string[]>
    try {
      if (asksToJoin) {
        await sdk.auth.requestToJoin({
          email, name, fields, language: i18n.language, ageConfirmed: true,
          ...(phone.phone ? { phone: phone.phone, phoneCountry: phone.phoneCountry } : {})
        })
        setRequestSent(true)
        return
      }
      /* Only with a phone and the box checked: its version records notices and news of each channel it covers. */
      const consentText = signupConsentOf(consentTexts)
      const smsConsent = consentText && phone.phone && phoneConsent ? { textVersion: consentText.version } : undefined
      await signUp(email, name, password, fields, {
        ageConfirmed: true,
        ...(phone.phone ? { phone: phone.phone, phoneCountry: phone.phoneCountry } : {}),
        ...(smsConsent ? { smsConsent } : {})
      })
      if (smsConsent) markLoginConsentSeen(community?.id, loginConsentVersion(consentTexts))
      /* With a phone, the last step confirms it by code; without the channel on here, it just closes. */
      if (phone.phone) setVerifyPhone(phone)
      else onOpenChange(false)
    } catch (err: unknown) {
      /* A 422 carries one error per field (`fields.<key>`); the ones that
         belong to no field on screen go to the message box. */
      const { status, param, data } = apiErrorMessage(err)
      if (status === 400 && param === 'ageConfirmed') {
        setAgeConfirmed(false)
        setAgeError(t('ageGate.required', { age: Number((data as { minimumAge?: number } | null)?.minimumAge) || minimumAge }))
        return
      }
      const byField = apiFieldErrors(err)
      const known = new Set(signupFields.map((f) => f.key))
      const placed = Object.fromEntries(Object.entries(byField).filter(([key]) => known.has(key)))
      setFieldErrors(placed)
      if (Object.keys(placed).length < Object.keys(byField).length || !Object.keys(byField).length) {
        setError(authFailureText(err, t) ?? apiErrorText(err, err instanceof Error ? err.message : String(err)))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 max-h-[90dvh] overflow-y-auto bg-white/80 backdrop-blur-2xl border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {asksToJoin ? t('request.title') : t('signupTitle')}
          </DialogTitle>
        </DialogHeader>
        {requestSent ? (
          <div className="p-8 text-center sm:p-10" data-testid="signup-request-sent">
            <MailCheck className="mx-auto mb-4 h-12 w-12 text-primary" />
            <h2 className="mb-2 text-2xl font-semibold text-gray-800">{t('request.sentTitle')}</h2>
            <p className="mb-6 text-base text-gray-600">{t('request.sentDescription', { name: community?.name || 'Memberfy' })}</p>
            <Button type="button" variant="secondary" className="h-11 w-full rounded-xl" onClick={() => onOpenChange(false)}>
              {t('request.close')}
            </Button>
          </div>
        ) : verifyPhone ? (
          <div className="p-8 sm:p-10" data-testid="signup-verify-phone">
            <h2 className="mb-2 flex items-center gap-2 text-2xl font-semibold text-gray-800">
              <Smartphone className="h-6 w-6 text-primary" />{t('sms:signup.verifyTitle')}
            </h2>
            <p className="mb-6 text-base text-gray-600">{t('sms:signup.verifyDescription')}</p>
            <PhoneVerification
              phone={verifyPhone.phone}
              phoneCountry={verifyPhone.phoneCountry}
              autoSend
              onVerified={() => onOpenChange(false)}
              onUnavailable={() => onOpenChange(false)}
            />
            <div className="mt-6 text-center">
              <button type="button" onClick={() => onOpenChange(false)} className="text-sm font-medium text-gray-500 hover:text-gray-700" data-testid="signup-verify-later">
                {t('sms:signup.later')}
              </button>
              <p className="mt-1 text-xs text-gray-400">{t('sms:signup.laterHint')}</p>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <div className="p-8 sm:p-10 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-500/[0.02] via-white/20 to-transparent pointer-events-none" />
            <h2 className="text-2xl font-semibold mb-2 text-gray-800">
              {asksToJoin ? t('request.title') : t('signupTitle')}
            </h2>
            <p className="text-gray-600 text-base mb-8">
              {asksToJoin ? t('request.description', { name: community?.name || 'Memberfy' }) : t('signupDescription', { name: community?.name || 'Memberfy' })}
            </p>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('namePlaceholder')}
                    className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-gray-800 text-base transition-all duration-200 rounded-xl"
                    required
                  />
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-gray-800 text-base transition-all duration-200 rounded-xl"
                    required
                  />
                </div>
                {!asksToJoin && (
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder={t('createPasswordPlaceholder')}
                    className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-gray-800 text-base transition-all duration-200 rounded-xl"
                    required
                    minLength={8}
                  />
                </div>
                )}
                {passwordError && !asksToJoin && (
                  <p className="text-sm text-red-500">{passwordError}</p>
                )}
                <div className="space-y-1" data-testid="signup-phone">
                  <label htmlFor="signup-phone" className="block text-sm text-gray-600">{t('sms:signup.phoneLabel')}</label>
                  <PhoneInput
                    id="signup-phone"
                    value={phone.phone}
                    country={phone.phoneCountry}
                    defaultCountry={countryFromLocale(community?.language)}
                    disabled={isLoading}
                    onChange={(next) => { setPhone(next); if (!next.phone) setPhoneConsent(false) }}
                  />
                </div>
                {!asksToJoin && phone.phone && signupConsentOf(consentTexts) && (
                  <label className="flex items-start gap-2 pt-1 text-sm text-gray-600" data-testid="signup-phone-consent">
                    <input
                      type="checkbox"
                      checked={phoneConsent}
                      onChange={(e) => setPhoneConsent(e.target.checked)}
                      disabled={isLoading}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary"
                    />
                    <span>{signupConsentOf(consentTexts)?.text}</span>
                  </label>
                )}
                {signupFields.length > 0 && (
                  <MemberFieldsForm
                    fields={signupFields}
                    values={fieldValues}
                    onChange={(next) => {
                      setFieldValues(next)
                      setFieldErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => next[key] === fieldValues[key])))
                    }}
                    disabled={isLoading}
                    errors={fieldErrors}
                    className="pt-2 sm:grid-cols-1"
                  />
                )}
                <AgeConfirmBox
                  id="signup-age-confirm"
                  age={minimumAge}
                  checked={ageConfirmed}
                  onChange={(next) => { setAgeConfirmed(next); if (next) setAgeError(null) }}
                  error={ageError}
                  disabled={isLoading}
                  className="pt-1"
                />
                <Button
                  type="submit"
                  disabled={isLoading || (!asksToJoin && !!passwordError)}
                  variant="primary"
                  className="w-full h-12 text-base rounded-xl"
                >
                  {isLoading ? (
                    asksToJoin ? t('request.sending') : t('creating')
                  ) : (
                    <>
                      {asksToJoin ? t('request.submit') : t('createAccountButton')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                <TermsNotice />
              </div>
              
              {error && (
                <div className={cn(
                  "backdrop-blur-sm border rounded-xl p-4",
                  error.includes('Check your email')
                    ? "bg-green-50/60 border-green-100/50"
                    : "bg-red-50/60 border-red-100/50"
                )}>
                  <p className={cn(
                    "text-base text-center font-light",
                    error.includes('Check your email')
                      ? "text-green-600"
                      : "text-red-500"
                  )}>{error}</p>
                </div>
              )}

              {/* The way back. The login dialog has always offered "no account?
                  sign up"; the return trip was missing, so anyone who opened
                  this by mistake — or who already had an account — had to close
                  the dialog and hunt for the header button. Mirrors the other
                  side down to the DOM trigger it clicks. */}
              <div className="text-center text-gray-600">
                <p>
                  {t('alreadyHaveAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      onOpenChange(false)
                      ;(document.querySelector('[data-login-trigger]') as HTMLButtonElement | null)?.click()
                    }}
                    className="font-medium text-primary hover:text-primary-600"
                  >
                    {t('signIn')}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </form>
        )}
      </DialogContent>
    </Dialog>
  )
}